import { useEffect, useMemo, useRef, useState } from "react";
import {
  collection,
  query,
  where,
  getDocs,
  doc,
  updateDoc,
  setDoc,
  deleteDoc,
} from "firebase/firestore";
import { db } from "../firebase";
import { useAuth } from "../AuthContext";
import { Link } from "react-router-dom";
import "./BusinessSection.css";
import {
  GoogleMap,
  Marker,
  Autocomplete,
  useLoadScript,
} from "@react-google-maps/api";

// const EMPTY_FORM = {
//   business_name: "",
//   code: "",
//   country: "",
//   island: "",
//   location: "",
//   categoryIds: [],
//   telephone: "",
//   email: "",
//   website: "",
//   is_antigua: false,
//   is_featured: false,
//   is_published: false,
// };


const EMPTY_FORM = {
  business_name: "",
  code: "",

  categoryIds: [],

  website: "",

  about_short: "",
  listingTier: "free",

  is_antigua: false,
  is_featured: false,
  is_published: false,

  locations: [
    {
      id: crypto.randomUUID(),

      country: "",
      island: "",
      region: "",
      address: "",

      telephone: "",
      email: "",
      telephones: [""],
      emails: [""],

      lat: "",
      lng: "",
    },
  ],
};

const limitWords = (value, maxWords = 60) => {
  const words = value.trim().split(/\s+/).filter(Boolean);
  return words.length <= maxWords ? value : words.slice(0, maxWords).join(" ");
};

function addLocation(formSetter) {
  formSetter((prev) => ({
    ...prev,
    locations: [
      ...prev.locations,
      {
        id: crypto.randomUUID(),

        country: "",
        island: "",
        region: "",
        address: "",

        telephone: "",
        email: "",
        telephones: [""],
        emails: [""],

        lat: "",
        lng: "",
      },
    ],
  }));
}

function removeLocation(formSetter, index) {
  formSetter((prev) => {
    if (prev.locations.length <= 1) return prev;

    return {
      ...prev,
      locations: prev.locations.filter((_, i) => i !== index),
    };
  });
}

function updateLocation(formSetter, index, field, value) {
  formSetter(prev => ({
    ...prev,
    locations: prev.locations.map((loc, i) =>
      i === index
        ? { ...loc, [field]: value }
        : loc
    ),
  }));
}

function updateLocationContact(formSetter, index, field, contactIndex, value) {
  formSetter((prev) => ({
    ...prev,
    locations: prev.locations.map((loc, i) => {
      if (i !== index) return loc;
      const values = [...(Array.isArray(loc[field]) ? loc[field] : [""])];
      values[contactIndex] = value;
      return { ...loc, [field]: values };
    }),
  }));
}

function LocationContacts({ formSetter, location, locationIndex }) {
  const fields = [
    { key: "telephones", legacy: "telephone", type: "tel", label: "Telephone" },
    { key: "emails", legacy: "email", type: "email", label: "Email" },
  ];

  return fields.map(({ key, legacy, type, label }) => {
    const values = Array.isArray(location[key]) ? location[key] : [location[legacy] || ""];
    return (
      <div className="location-contact-editor" key={key}>
        {values.map((value, contactIndex) => (
          <div className="location-contact-row" key={contactIndex}>
            <input type={type} placeholder={label} value={value} onChange={(e) => updateLocationContact(formSetter, locationIndex, key, contactIndex, e.target.value)} />
            {values.length > 1 && (
              <button type="button" onClick={() => formSetter((prev) => ({ ...prev, locations: prev.locations.map((loc, i) => i === locationIndex ? { ...loc, [key]: values.filter((_, j) => j !== contactIndex) } : loc) }))}>Remove</button>
            )}
          </div>
        ))}
        <button type="button" onClick={() => formSetter((prev) => ({ ...prev, locations: prev.locations.map((loc, i) => i === locationIndex ? { ...loc, [key]: [...values, ""] } : loc) }))}>Add {label.toLowerCase()}</button>
      </div>
    );
  });
}

// Location Address Input
function LocationAddressInput({ value, onAddress, onPick }) {
  const [autocomplete, setAutocomplete] = useState(null);

  return (
    <Autocomplete
      onLoad={(auto) => setAutocomplete(auto)}
      onPlaceChanged={() => {
        if (!autocomplete) return;

        const place = autocomplete.getPlace();
        if (!place?.geometry?.location) return;

        onPick(
          place.formatted_address || "",
          place.geometry.location.lat(),
          place.geometry.location.lng()
        );
      }}
    >
      <input
        type="text"
        placeholder="Enter address..."
        value={value}
        onChange={(e) => onAddress(e.target.value)}
      />
    </Autocomplete>
  );
}

function getBusinessLocations(biz) {
  if (Array.isArray(biz.locations) && biz.locations.length > 0) {
    return biz.locations.filter(Boolean).map((location) => ({
      ...location,
      telephones: Array.isArray(location.telephones) ? location.telephones : [location.telephone || ""],
      emails: Array.isArray(location.emails) ? location.emails : [location.email || ""],
    }));
  }

  return [
    {
      country: biz.country || "",
      island: biz.island || "",
      region: "",
      address: biz.location?.address || biz.locationLabel || "",
      telephone: biz.telephone || "",
      email: biz.email || "",
      telephones: [biz.telephone || ""],
      emails: [biz.email || ""],
      lat: biz.location?.lat || biz.location?.latitude || "",
      lng: biz.location?.lng || biz.location?.longitude || "",
    },
  ];
}

export default function BusinessSection() {
  const libraries = ["places"];

  const { isLoaded } = useLoadScript({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    libraries,
  });
    const { user } = useAuth();

  const [businesses, setBusinesses] = useState([]);
  const [loading, setLoading] = useState(true);

  const [cats, setCats] = useState([]);

  // separate state: one for editing, one for adding
  const [editForm, setEditForm] = useState(EMPTY_FORM);
  const [newForm, setNewForm] = useState(EMPTY_FORM);
  const [editingId, setEditingId] = useState(null);

  // Loading Islands and Countries from Firebase
  const [islands, setIslands] = useState([]);
  const [countries, setCountries] = useState([]);
  const businessIslandOptions = useMemo(
    () => buildBusinessIslandOptions(islands, countries),
    [islands, countries]
  );

 
  const islandsForNewCountry = useMemo(() => {
    return [...new Set(
      businesses
        .filter((b) => b.country === newForm.country)
        .map((b) => b.island)
        .filter(Boolean)
    )].sort();
  }, [businesses, newForm.country]);

  const islandsForEditCountry = useMemo(() => {
    return [...new Set(
      businesses
        .filter((b) => b.country === editForm.country)
        .map((b) => b.island)
        .filter(Boolean)
    )].sort();
  }, [businesses, editForm.country]);



  // GET COUNTRY
  function getCountryFromIsland(islandName) {
    const search = normalizeIslandName(islandName);

    const match = countries.find((country) =>
      normalizeIslandName(country.name) === search ||
      (
        Array.isArray(country.slugs) &&
        country.slugs.some(
          (slug) =>
            normalizeIslandName(
              typeof slug === "object" ? slug.name || slug.slug : slug
            ) === search
        )
      )
    );

    return match?.name || "";
  }


  /* ========= LOAD BUSINESSES ========= */
  useEffect(() => {
    if (!user) return;

    async function loadBusinesses() {
      setLoading(true);
      try {
        const qBiz = query(
          collection(db, "businesses"),
          where("ownerId", "==", user.uid)
        );
        const snap = await getDocs(qBiz);
        const list = [];
        snap.forEach((d) => list.push({ id: d.id, ...d.data() }));
        setBusinesses(list);
      } finally {
        setLoading(false);
      }
    }

    loadBusinesses();
  }, [user]);

  /* ========= LOAD CATEGORIES (enabled) ========= */
  useEffect(() => {
    async function loadCats() {
      try {
        const cq = query(
          collection(db, "categories"),
          where("enabled", "==", true)
        );
        const snap = await getDocs(cq);
        const list = [];
        snap.forEach((d) => list.push({ id: d.id, ...d.data() }));
        list.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
        setCats(list);
      } catch (err) {
        console.error("Failed to load categories", err);
      }
    }

    loadCats();
  }, []);

  /* Load Islands */

  useEffect(() => {
    async function loadIslands() {
      try {
        const snap = await getDocs(collection(db, "islands"));

        const list = [];
        snap.forEach((d) => {
          const data = d.data();

          if (data.enabled !== false) {
            list.push({
              id: d.id,
              ...data,
            });
          }
        });

        list.sort((a, b) =>
          (a.name || "").localeCompare(b.name || "")
        );

        setIslands(list);
      } catch (err) {
        console.error("Failed to load islands", err);
      }
    }

    loadIslands();
  }, []);


  /* LOAD COUNTRIES */
  useEffect(() => {
    async function loadCountries() {
      try {
        const snap = await getDocs(collection(db, "country"));

        const list = [];

        snap.forEach((doc) => {
          list.push({
            id: doc.id,
            ...doc.data(),
          });
        });

        list.sort((a, b) =>
          (a.name || "").localeCompare(b.name || "")
        );
        console.log("COUNTRIES", list);
        setCountries(list);
      } catch (err) {
        console.error("Failed to load countries", err);
      }
    }

    loadCountries();
  }, []);



  
  /* ========= EDITING ========= */
  

  

  const handleEditChange = (e) => {
  const { name, value, type, checked } = e.target;

  setEditForm((prev) => {
    return {
      ...prev,
      [name]: name === "about_short"
        ? limitWords(value)
        : type === "checkbox" ? checked : value,
    };
  });
  };

  const toggleEditCategory = (cid) => {
    setEditForm((prev) => {
      const current = Array.isArray(prev.categoryIds) ? prev.categoryIds : [];
      return current.includes(cid)
        ? { ...prev, categoryIds: current.filter((x) => x !== cid) }
        : { ...prev, categoryIds: [...current, cid] };
    });
  };

  const startEdit = (biz) => {
    setEditingId(biz.id);
    setEditForm({
      business_name: biz.business_name || "",
      code: biz.code || "",
      // country: biz.country || "",
      // island: biz.island || "",
      // location: biz.location || "",
      categoryIds: Array.isArray(biz.categoryIds) ? biz.categoryIds : [],
      // telephone: biz.telephone || "",
      // email: biz.email || "",
      locations: getBusinessLocations(biz),
      website: biz.website || "",
      about_short: biz.about_short || "",
      listingTier: biz.listingTier || (biz.is_featured ? "featured" : "free"),
      is_antigua: !!biz.is_antigua,
      is_featured: !!biz.is_featured,
      is_published: !!biz.is_published,
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditForm(EMPTY_FORM);
  };

  const saveEdit = async (id) => {
    try {
      await updateDoc(doc(db, "businesses", id), { ...editForm });
      setBusinesses((prev) =>
        prev.map((b) => (b.id === id ? { ...b, ...editForm } : b))
      );
      setEditingId(null);
      setEditForm(EMPTY_FORM);
    } catch (err) {
      console.error("Error saving business:", err);
      alert("Failed to save business: " + err.message);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this business?")) return;
    try {
      await deleteDoc(doc(db, "businesses", id));
      setBusinesses((prev) => prev.filter((b) => b.id !== id));
    } catch (err) {
      console.error("Error deleting business:", err);
      alert("Failed to delete business: " + err.message);
    }
  };

  /* ========= ADD NEW ========= */

  const handleNewChange = (e) => {
    const { name, value, type, checked } = e.target;

    setNewForm((prev) => {
      return {
        ...prev,
        [name]: name === "about_short"
          ? limitWords(value)
          : type === "checkbox" ? checked : value,
      };
    });
  };

  const toggleNewCategory = (cid) => {
    setNewForm((prev) => {
      const current = Array.isArray(prev.categoryIds) ? prev.categoryIds : [];
      return current.includes(cid)
        ? { ...prev, categoryIds: current.filter((x) => x !== cid) }
        : { ...prev, categoryIds: [...current, cid] };
    });
  };

  const resetNewForm = () => setNewForm(EMPTY_FORM);

  const handleAdd = async () => {
    if (!user) {
      alert("You must be logged in to add a business.");
      return;
    }
    if (!newForm.business_name.trim()) {
      alert("Business name is required.");
      return;
    }

    try {
      
      const businessId = newForm.business_name.trim();
      await setDoc(doc(db, "businesses", businessId), {
        ...newForm,
        ownerId: user.uid,
      });

      setBusinesses((prev) => [
        ...prev,
        { id: businessId, ...newForm, ownerId: user.uid },
      ]);
      resetNewForm();
    } catch (err) {
      console.error("Error adding business:", err);
      alert("Failed to add business: " + err.message);
    }
  };
  if (!isLoaded) {
    return <div>Loading Maps...</div>;
  }
  /* ========= RENDER ========= */

  return (
   
    <div className="business-section">
      <h2>Your Businesses</h2>

      {loading ? (
        <p>Loading...</p>
      ) : businesses.length === 0 ? (
        <p>No businesses yet. Add one below!</p>
      ) : (
        <ul className="business-list">
          {businesses.map((biz) => (
            <li key={biz.id}>
              {editingId === biz.id ? (
                <div className="business-form">
                  <input
                    type="text"
                    name="business_name"
                    placeholder="Business Name"
                    value={editForm.business_name}
                    onChange={handleEditChange}
                  />
                  {/* <input
                    type="text"
                    name="code"
                    placeholder="Code"
                    value={editForm.code}
                    onChange={handleEditChange}
                  /> */}
                  {editForm.locations.map((loc, index) => (
                    <div key={index} className="location-card">

                      <h4>Location {index + 1}</h4>

                      <input
                        type="text"
                        className="country-input-readonly"
                        value={loc.country}
                        placeholder="Country"
                        readOnly
                      />

                      <IslandSelector
                        islands={businessIslandOptions}
                        value={loc.island}
                        onChange={(value) => {
                          updateLocation(
                            setEditForm,
                            index,
                            "island",
                            value
                          );

                          updateLocation(
                            setEditForm,
                            index,
                            "country",
                            getCountryFromIsland(value)
                          );
                        }}
                      />

    <LocationContacts formSetter={setEditForm} location={loc} locationIndex={index} />

    <LocationAddressInput
      value={loc.address}
      onAddress={(value) =>
        updateLocation(setEditForm, index, "address", value)
      }
      onPick={(formatted, lat, lng) => {
        updateLocation(setEditForm, index, "address", formatted);
        updateLocation(setEditForm, index, "lat", lat);
        updateLocation(setEditForm, index, "lng", lng);
      }}
    />

      {editForm.locations.length > 1 && (
        <button
          type="button"
                onClick={() =>
                  removeLocation(setEditForm, index)
                }
              >
                Remove Location
              </button>
            )}

          </div>
        ))}

        <button
                    type="button"
                    onClick={() => addLocation(setEditForm)}
                  >
                    + Add Location
        </button>

                  <input
                    type="url"
                    name="website"
                    placeholder="Website"
                    value={editForm.website}
                    onChange={handleEditChange}
                  />

                   {/* Category Fields */}
                  <div className="field">
                    <label>Categories</label>
                    <CategorySelector
                      cats={cats}
                      selected={editForm.categoryIds}
                      onToggle={toggleEditCategory}
                    />
                  </div>

                  <div className="field">
                    <label>Listing type</label>
                    <div className="listing-tier-readonly">
                      {(editForm.listingTier || "free").replace(/^./, (letter) => letter.toUpperCase())} listing
                    </div>
                  </div>

                  <div className="field">
                    <label>Description (60 words max)</label>
                    <textarea
                      name="about_short"
                      rows={4}
                      placeholder="Describe this business listing"
                      value={editForm.about_short}
                      onChange={handleEditChange}
                    />
                  </div>

                  <div className="checkboxes">
                    <label>
                      <input
                        type="checkbox"
                        name="is_published"
                        checked={editForm.is_published}
                        onChange={handleEditChange}
                      />{" "}
                      Published
                    </label>
                  </div>

                  <div className="business-edit-actions">
                    <button onClick={() => saveEdit(biz.id)}>Save</button>
                    <button onClick={cancelEdit}>Cancel</button>
                    <Link
                      to={`/business/${biz.id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="business-view-link"
                    >
                      View business page
                    </Link>
                  </div>
                </div>
              ) : (
                <>
                  <div>
                    <strong>{biz.business_name}</strong>

                    {biz.website && (
                      <div>
                        <strong>Website:</strong> {biz.website}
                      </div>
                    )}
                    <div>
                      <strong>Listing type:</strong>{" "}
                      {(biz.listingTier || "free").replace(/^./, (letter) => letter.toUpperCase())}
                    </div>
                    <div>
                      <strong>Locations:</strong>
                      <ul className="business-location-summary">
                        {getBusinessLocations(biz).map((loc, index) => (
                          <li key={index}>
                            {[loc.region, loc.island, loc.country]
                              .filter(Boolean)
                              .join(", ") || loc.address || `Location ${index + 1}`}
                            {loc.telephone ? ` - ${loc.telephone}` : ""}
                            {loc.email ? ` - ${loc.email}` : ""}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                  <div>
                    <button onClick={() => startEdit(biz)}>Edit</button>
                    <button onClick={() => handleDelete(biz.id)}>Delete</button>
                  </div>
                </>
              )}
            </li>
          ))}
        </ul>
      )}

      {/* ADD NEW BUSINESS */}
      <div className="business-form">
        <h3>Add New Business</h3>
        <input
          type="text"
          name="business_name"
          placeholder="Business Name"
          value={newForm.business_name}
          onChange={handleNewChange}
        />
        {/* <input
          type="text"
          name="code"
          placeholder="Code"
          value={newForm.code}
          onChange={handleNewChange}
        /> */}
        
        {newForm.locations.map((loc, index) => (
          <div key={index} className="location-card">

            <h4>Location {index + 1}</h4>

            <input
              type="text"
              className="country-input-readonly"
              value={loc.country}
              placeholder="Country"
              readOnly
            />

            <IslandSelector
              islands={businessIslandOptions}
              value={loc.island}
              onChange={(value) => {
                updateLocation(
                  setNewForm,
                  index,
                  "island",
                  value
                );

                updateLocation(
                  setNewForm,
                  index,
                  "country",
                  getCountryFromIsland(value)
                );
              }}
            />

            <LocationAddressInput
              value={loc.address}
              onAddress={(value) =>
                updateLocation(setNewForm, index, "address", value)
              }
              onPick={(formatted, lat, lng) => {
                updateLocation(setNewForm, index, "address", formatted);
                updateLocation(setNewForm, index, "lat", lat);
                updateLocation(setNewForm, index, "lng", lng);
              }}
            />

            <LocationContacts formSetter={setNewForm} location={loc} locationIndex={index} />

            {newForm.locations.length > 1 && (
              <button
                type="button"
                onClick={() => removeLocation(setNewForm, index)}
              >
                Remove Location
              </button>
            )}

          </div>
        ))}

        <button
          type="button"
          onClick={() => addLocation(setNewForm)}
        >
          + Add Location
        </button>



        <input
          type="text"
          name="website"
          placeholder="Website"
          value={newForm.website}
          onChange={handleNewChange}
        />

        <div className="field">
          <label>Categories</label>
          <CategorySelector
            cats={cats}
            selected={newForm.categoryIds}
            onToggle={toggleNewCategory}
          />
        </div>

        <div className="field">
          <label>Listing type</label>
          <div className="listing-tier-readonly">Free listing</div>
        </div>

        <div className="field">
          <label>Description (60 words max)</label>
          <textarea
            name="about_short"
            rows={4}
            placeholder="Describe this business listing"
            value={newForm.about_short}
            onChange={handleNewChange}
          />
        </div>

        <div className="checkboxes">
          <label>
            <input
              type="checkbox"
              name="is_published"
              checked={newForm.is_published}
              onChange={handleNewChange}
            />{" "}
            Published
          </label>
        </div>

        <button onClick={handleAdd}>Add Business</button>
      </div>
    </div>
  );
}


// ISLAND SECLECTOR  (check tmr)*/ 
// ISLAND SECLECTOR  (check tmr)*/ 
function IslandSelector({ islands, value, onChange }) {
    const [q, setQ] = useState(value || "");
    const [open, setOpen] = useState(false);

    const filtered = useMemo(() => {
      const k = q.toLowerCase().trim();

      return islands
        .filter((i) =>
          (i.name || "").toLowerCase().includes(k)
        )
        .slice(0, 10);
    }, [q, islands]);
    const boxRef = useRef(null);

    useEffect(() => {
      function handleClickOutside(event) {
        if (
          boxRef.current &&
          !boxRef.current.contains(event.target)
        ) {
          setOpen(false);
        }
      }

      document.addEventListener("mousedown", handleClickOutside);

      return () =>
        document.removeEventListener(
          "mousedown",
          handleClickOutside
        );
    }, []);
    useEffect(() => {
      setQ(value || "");
    }, [value]);

    return (
      <div className="island-picker" ref={boxRef}>
        <input
          type="text"
          placeholder="Type island name..."
          value={q}
          onChange={(e) => {
            setQ(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
        />

        {open && filtered.length > 0 && (
          <div className="island-suggest">
            {filtered.map((island) => (
              <div
                key={island.id}
                className="island-suggest-item"
                onMouseDown={() => {
                  onChange(island.name);
                  setQ(island.name);
                  setOpen(false);
                }}
              >
                {island.name}
              </div>
            ))}
          </div>
        )}
      </div>
    );
}


function normalizeIslandName(value = "") {
  return String(value)
    .toLowerCase()
    .replace(/\bsaint\b/g, "st")
    .replace(/[^a-z0-9]/g, "");
}

function buildBusinessIslandOptions(islands, countries) {
  const options = new Map();
  const countryNames = new Set();

  countries.forEach((country) => {
    countryNames.add(normalizeIslandName(country.name));

    const slugs = (Array.isArray(country.slugs) ? country.slugs : [])
      .map((entry) => typeof entry === "object" ? entry.name || entry.slug : entry)
      .filter(Boolean)
      .map(String);
    const uniqueSlugs = [...new Map(
      slugs.map((name) => [normalizeIslandName(name), name])
    ).values()];
    const individualIslands = uniqueSlugs.filter(
      (name) => normalizeIslandName(name) !== normalizeIslandName(country.name)
    );
    const namesToAdd = individualIslands.length > 0
      ? individualIslands
      : [uniqueSlugs[0] || country.name];

    namesToAdd.forEach((name) => {
      options.set(normalizeIslandName(name), {
        id: `country-${country.id}-${normalizeIslandName(name)}`,
        name,
      });
    });
  });

  islands.forEach((island) => {
    const key = normalizeIslandName(island.name);
    if (!countryNames.has(key) && !options.has(key)) {
      options.set(key, island);
    }
  });

  return [...options.values()].sort((a, b) =>
    (a.name || "").localeCompare(b.name || "")
  );
}

/* -------- Category selector (copied from BusinessPage) -------- */


function CategorySelector({ cats, selected, onToggle }) {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const boxRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    function onDocClick(e) {
      if (boxRef.current && !boxRef.current.contains(e.target)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const filtered = useMemo(() => {
    const k = q.trim().toLowerCase();
    const safeSelected = Array.isArray(selected) ? selected : [];

    const notSelected = (c) => !safeSelected.includes(c.id);
    const byName = (a, b) => (a.name || "").localeCompare(b.name || "");

    const getKeywordsText = (keywords) => {
      if (Array.isArray(keywords)) return keywords.join(" ");
      if (typeof keywords === "string") return keywords;
      if (keywords && typeof keywords === "object") {
        return Object.values(keywords).join(" ");
      }
      return "";
    };
    if (!k) return cats.filter(notSelected).sort(byName).slice(0, 10);

    const hit = cats.filter((c) => {
      const n = (c.name || "").toLowerCase();
      const kw = getKeywordsText(c.keywords).toLowerCase();

      return (
        !safeSelected.includes(c.id) &&
        (n.includes(k) || kw.includes(k))
      );
    });

    return hit.sort(byName).slice(0, 10);
  }, [q, cats, selected]);

  

  function add(id) {
    const safeSelected = Array.isArray(selected) ? selected : [];
    if (!safeSelected.includes(id)) onToggle(id);
    setQ("");
    setActive(0);
    setOpen(false);
    inputRef.current?.focus();
  }

  function remove(id) {
    const safeSelected = Array.isArray(selected) ? selected : [];
    if (safeSelected.includes(id)) onToggle(id);
    inputRef.current?.focus();
  }

  function onKeyDown(e) {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setOpen(true);
      setActive((i) => (filtered.length ? (i + 1) % filtered.length : 0));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setOpen(true);
      setActive((i) =>
        filtered.length ? (i - 1 + filtered.length) % filtered.length : 0
      );
    } else if (e.key === "Enter") {
      if (open && filtered[active]) {
        e.preventDefault();
        add(filtered[active].id);
      }
    } else if (e.key === "Escape") {
      setOpen(false);
    } else if (e.key === "Backspace" && q === "" && selected.length > 0) {
      remove(selected[selected.length - 1]);
    }
  }

  return (
    <div className="catPickerNice" ref={boxRef}>
      <div
        className="catPickerInput"
        onClick={() => inputRef.current?.focus()}
        role="combobox"
        aria-expanded={open}
        aria-haspopup="listbox"
      >
        {selected.map((id) => {
          const c = cats.find((x) => x.id === id);
          const label = c?.name || id;
          return (
            <span key={id} className="chip chipSelected" title={label}>
              {label}
              <button
                type="button"
                className="chipX"
                onClick={(e) => {
                  e.stopPropagation();
                  remove(id);
                }}
                aria-label={`Remove ${label}`}
              >
                ×
              </button>
            </span>
          );
        })}
        <input
          ref={inputRef}
          className="catInput"
          placeholder={
            selected.length ? "Add another category…" : "Add category…"
          }
          value={q}
          onChange={(e) => {
            setQ(e.target.value);
            setOpen(true);
            setActive(0);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKeyDown}
        />
      </div>

      {open && (
        <div className="catSuggest" role="listbox">
          {filtered.length === 0 ? (
            <div className="catSuggestEmpty">No matches.</div>
          ) : (
            filtered.map((c, i) => (
              <div
                key={c.id}
                role="option"
                aria-selected={i === active}
                className={`catSuggestItem ${i === active ? "active" : ""}`}
                onMouseDown={(e) => {
                  e.preventDefault();
                  add(c.id);
                }}
                onMouseEnter={() => setActive(i)}
              >
                <div className="catName">{c.name}</div>
                {Array.isArray(c.keywords) && c.keywords.length > 0 && (
                  <div className="catKw">
                    {c.keywords.slice(0, 4).join(" • ")}
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}
